// ResortReservations.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Spinner } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";

import AddReservation from "./AddReservation";
import ReservationFilters from "./ReservationFilters";
import { reservationMatchesSearch } from "./ReservationSearch";
import ReservationStats from "./ReservationStats";
import ReservationTable from "./ReservationTable";
import ReservationExportButton from "./ReservationExportButton";
import { deleteContent, getContents, getRoomPlans } from "../../../FIREBASECONFIG";

import { getUserRole } from "../../../APIS";
import { STATUS_META } from "./reservationViewUtils";

const getCurrentWeekRange = () => ({
  startDate: moment().startOf("isoWeek").format("YYYY-MM-DD"),
  endDate: moment().endOf("isoWeek").format("YYYY-MM-DD"),
});

const ResortReservations = () => {
  const collectionName = "reservations";
  const history = useHistory();
  const location = useLocation();
  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || "{}");
  const userHotelId = userDetails.hotelId || "";
  const hotelDropdownLocked = Boolean(userHotelId);
  const role = getUserRole();

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => {
    const { startDate, endDate } = getCurrentWeekRange();
    return {
      searchTerm: "",
      status: "All",
      source: "",
      startDate,
      endDate,
      hotelId: "",
      roomTypeId: "",
      roomPlanId: "",
    };
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editReservation, setEditReservation] = useState(null);
  const [hotelOptions, setHotelOptions] = useState([]);
  const [roomTypeOptions, setRoomTypeOptions] = useState([]);
  const [roomPlanOptions, setRoomPlanOptions] = useState([]);
  const tableRef = useRef(null);
  const handledEditRequestRef = useRef(false);

  /** 🔹 Fetch Reservations */
  const fetchReservations = async () => {
    try {
      setLoading(true);
      const data = await getContents(collectionName);
      setReservations(data || []);
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  /** 🔹 Open the edit form when arriving from the reservation view screen */
  useEffect(() => {
    const requestedId = location.state?.editReservationId;
    if (!requestedId || handledEditRequestRef.current || !reservations.length) return;

    const match = reservations.find((r) => r.id === requestedId);
    if (!match) return;

    handledEditRequestRef.current = true;
    setEditReservation(match);
    setShowForm(true);
    history.replace({ pathname: location.pathname, state: null });
  }, [location.state, location.pathname, reservations, history]);

  /** 🔹 Fetch Hotels */
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        let Hotels = await getContents("hotels");
        if (hotelDropdownLocked) {
          const match = Hotels.filter((h) => h.id === userHotelId);
          Hotels = match.length
            ? match
            : [{ id: userHotelId, name: userHotelId }];
          setFilters((prev) => ({ ...prev, hotelId: userHotelId }));
        }

        setHotelOptions(
          Hotels.map((h) => ({ label: h.name, value: h.id, rawData: h }))
        );
      } catch (err) {
        console.error("Hotel fetch error:", err);
      }
    };
    fetchHotels();
  }, [hotelDropdownLocked, userHotelId]);

  const selectedHotelId = userHotelId || filters.hotelId;

  /** 🔹 Fetch room types & plans when a hotel is selected */
  useEffect(() => {
    const fetchRoomFilterOptions = async () => {
      if (!selectedHotelId) {
        setRoomTypeOptions([]);
        setRoomPlanOptions([]);
        return;
      }

      try {
        const allRooms = await getContents("hotel_rooms");
        const hotelRooms = allRooms.filter((r) => r.hotel_id === selectedHotelId);

        setRoomTypeOptions(
          hotelRooms.map((r) => ({ label: r.name, value: r.id }))
        );

        const plansByRoom = await Promise.all(
          hotelRooms.map(async (room) => {
            const plans = await getRoomPlans(room.id);
            return plans.map((plan) => ({
              label: plan.name || plan.planName,
              value: plan.id,
              roomId: room.id,
            }));
          })
        );

        setRoomPlanOptions(plansByRoom.flat());
      } catch (err) {
        console.error("Room filter options fetch error:", err);
        setRoomTypeOptions([]);
        setRoomPlanOptions([]);
      }
    };

    fetchRoomFilterOptions();
  }, [selectedHotelId]);

  /** 🔹 Filtered Reservations */

  const filteredReservations = useMemo(() => {
    const { status, source, startDate, endDate, hotelId, roomTypeId, roomPlanId } =
      filters;
    const hasSearch = Boolean(String(searchQuery || "").trim());

    const filterData = reservations.filter((r) => {
      // Free-text search finds across all dates; week range only applies when idle.
      let matchDate = true;
      if (!hasSearch) {
        switch (true) {
          case !!startDate && !!endDate:
            matchDate =
              moment(r.checkIn, "YYYY-MM-DD").isSameOrAfter(
                moment(startDate, "YYYY-MM-DD"),
                "day",
              ) &&
              moment(r.checkIn, "YYYY-MM-DD").isSameOrBefore(
                moment(endDate, "YYYY-MM-DD"),
                "day",
              );
            break;
          case !!startDate:
            matchDate = moment(r.checkIn, "YYYY-MM-DD").isSameOrAfter(
              moment(startDate, "YYYY-MM-DD"),
              "day",
            );
            break;
          default:
            matchDate = true;
        }
      }

      const matchSearch = reservationMatchesSearch(r, searchQuery);

      const matchStatus =
        status === "All" ||
        r.reservationType === status ||
        (status === "Cancelled" &&
          (r.reservationType === "Cancelled" ||
            r.reservationType === "Cancelled (hide)"));

      const matchSource = !source || r.source === source;

      let matchHotel = true;
      if (userHotelId) {
        matchHotel = r.hotelId === userHotelId;
      } else if (hotelId) {
        matchHotel = r.hotelId === hotelId;
      }

      const matchRoomType =
        !roomTypeId ||
        (r.roomDetails || []).some((detail) => detail.id === roomTypeId);

      const matchRoomPlan =
        !roomPlanId ||
        (r.roomDetails || []).some((detail) => detail.planId === roomPlanId);

      return (
        matchSearch &&
        matchStatus &&
        matchSource &&
        matchDate &&
        matchHotel &&
        matchRoomType &&
        matchRoomPlan
      );
    });

    return filterData;
  }, [
    reservations,
    searchQuery,
    filters.startDate,
    filters.endDate,
    filters.status,
    filters.source,
    filters.hotelId,
    filters.roomTypeId,
    filters.roomPlanId,
    userHotelId,
  ]);

  /** 🔹 Actions */
  const handleDelete = async (id) => {
    try {
      await deleteContent(collectionName, id);
      setReservations((prev) => prev.filter((r) => r.id !== id));
    } catch (error) {
      console.error("Error deleting reservation:", error);
    }
  };

  const handleView = (item) => {
    history.push(`/Reservations/view?id=${item.id}`, { reservation: item });
  };

  const toggleForm = async () => {
    if (showForm) await fetchReservations();
    setShowForm((prev) => !prev);
    setEditReservation(null);
  };

  return (
    <div className="col-12">
      {showFilters && !showForm && (
        <div className="card mb-3">
          <div className="card-body border-bottom">
            <ReservationFilters
              filters={filters}
              setFilters={setFilters}
              hotelOptions={hotelOptions}
              disableHotelSelect={hotelDropdownLocked}
              userHotelId={userHotelId}
              getDefaultDateRange={getCurrentWeekRange}
              selectedHotelId={selectedHotelId}
              roomTypeOptions={roomTypeOptions}
              roomPlanOptions={roomPlanOptions}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onClose={() => setShowFilters(false)}
            />
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h4 className="mb-0">Hotel Reservations</h4>
          <div className="d-flex align-items-center gap-2">
            {!showForm && (
              <>
                <button
                  type="button"
                  className={`btn ${showFilters ? "btn-primary" : "btn-secondary"}`}
                  onClick={() => setShowFilters((prev) => !prev)}
                >
                  <i className="fa-solid fa-filter me-2" />
                  Filters
                </button>
                <ReservationExportButton
                  tableRef={tableRef}
                  reservations={filteredReservations}
                  disabled={loading}
                />
              </>
            )}
            <button className="btn btn-secondary" onClick={toggleForm}>
              {!showForm && <i className="fa-solid fa-plus me-2" />}
              {showForm ? "Close" : ""}
            </button>
          </div>
        </div>
        {/* Stats describe the list, so they stay out of the create/edit form. */}
        {!showForm && <ReservationStats reservations={filteredReservations} />}

        {/* Body */}
        <div className="card-body">
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "200px" }}
            >
              <Spinner animation="border" />
            </div>
          ) : showForm ? (
            <AddReservation
              role={role}
              userDetails={userDetails}
              reservation={editReservation}
              toggleForm={toggleForm}
            />
          ) : (
            <ReservationTable
              ref={tableRef}
              reservations={filteredReservations}
              onView={handleView}
              handleDelete={handleDelete}
            />
          )}
        </div>
      </div>

      {!showForm && (
        <div className="card mt-3">
          <div className="card-header">
            <h5 className="mb-0">Status Legend</h5>
          </div>
          <div className="card-body">
            <div className="d-flex flex-wrap gap-3 gap-md-4">
              {Object.values(STATUS_META).map((meta) => (
                <div
                  key={meta.label}
                  className="d-flex align-items-center gap-2"
                >
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      background: meta.background,
                      border: `1px solid ${meta.color}`,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontWeight: 600, color: meta.color }}>
                    {meta.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResortReservations;
